import Emitter from 'events';
import _ from 'lodash';
import CollectionUploadOperation from '../protocol/CollectionUploadOperation';
import MainThreadUploadWorker from '../workers/UploadWorker';
import { ImportQueryQuery, ImportQueryMsg, ImportQueryResponse } from '../autogenerated/ZoomBridge';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';
import ErrorHandler from '../error/ErrorHandler';
import ErrorId from '../protocol/ErrorId';
import Store from '../store/Store';
import { ASSET_LIST_BLOCK_INSTANCE } from '../store/Constants';

const debug = require('../debug/debug')('wc:controllers:ImportController');

const debouncedRender = _.debounce(() => window.render(), 200, { maxWait: 200 });
let i = 1;

export default class ImportController extends Emitter {
  static instance = null;
  static timerGap = 200;

  _uploadings = {};
  _collectionUploadOperations = [];
  _importInProgress = false;
  _timerCount = 0;
  _unsupportedCharacters = /[*?|:"<>]/;

  constructor() {
    super();
    if (ImportController.instance === null) {
      ImportController.instance = this;
    }
    return ImportController.instance;
  }

  static getInstance() {
    if (ImportController.instance === null) {
      ImportController.instance = new ImportController();
    }
    return ImportController.instance;
  }

  issueCheckInQuery(query) {
    this.runClientImport(query).then(
      function (success: boolean) {
        console.debug('Project Details:', success);
        window.gs.setUploadSuccess('Successfully uploaded');
        const assetListBlockRef = Store.get(ASSET_LIST_BLOCK_INSTANCE);
        if (assetListBlockRef) assetListBlockRef.onRefreshClick();
      },
      function (error) {
        console.debug(error);
        ErrorHandler.handleError(error);
      }
    );
  }

  runClientImport(query) {
    return new Promise((resolve, reject) => {
      this.lockImportQuery();
      window.gs.setProgress(true);
      query.run().then(
        (resp) => {
          window.gs.setProgress(false);
          debug('[issueImportQuery()] Success. Response: ', resp);
          this.unlockImportQuery();
          if (resolve) resolve(resp);
        },
        (error) => {
          window.gs.setProgress(false);
          this.unlockImportQuery();
          if (reject) reject(error);
        }
      );
    });
  }

  createUploading(file, collectionId, isCollectionItem = false, uniqueKey = this.getUniqueKey()) {
    const isNameValid = !this._unsupportedCharacters.test(file.name);
    var uploading = {
      id: i++,
      uniqueKey: uniqueKey,
      name: file.name,
      status: isNameValid ? t('UPLOADING.UPLOADING') : t('UPLOADING.ERROR'),
      imported: isNameValid ? false : true,
      loaded: 0,
      total: 0,
      errorText: isNameValid ? null : t('UPLOADING.INVALID_CHAR_IN_FILE_NAME'),
      preview: null,
      type: file.type,
      isCollectionItem
    };

    if (isCollectionItem) {
      uploading.collectionId = collectionId;
    } else {
      this._uploadings[uploading.id] = uploading;
    }

    if (isNameValid) {
      debug('[createUploading()] Starting upload: ', uploading);

      this._notifyWorker('START_UPLOAD', { uploading, file });

      if (file.type.indexOf('image') > -1) this._notifyWorker('READ_IMAGE', { uploading });
      else if (file.type.indexOf('video') > -1) uploading.preview = ThemeSetup.getImageURL('icon_video.svg');
      else if (file.type.indexOf('pdf') > -1) uploading.preview = 'images/icon_pdf.svg';
      else uploading.preview = ThemeSetup.getImageURL('icon_file.svg');

      uploading.abort = () => {
        let clone = { ...uploading };
        delete clone.abort;
        debug('[createUploading()] Aborting upload: ', uploading);
        this._notifyWorker('STOP_UPLOAD', { uploading: clone });
      };
    } else {
      uploading.preview = ThemeSetup.getImageURL('icon_file.svg');
    }

    return uploading;
  }

  setUploadings(arr) {
    this._uploadings = {};
    arr.forEach((u) => (this._uploadings[u.id] = u));
  }

  removeUploading(id) {
    delete this._uploadings[id];
    this._timerCount--;
  }

  removeAllUploadings() {
    this._notifyWorker('CLEAR_UPLOADS');
    this._uploadings = {};
    this._timerCount = 0;
    this.resetUniqueKey();
  }

  getUniqueKey() {
    if (this.uniqueKey === undefined) {
      this.uniqueKey = `${Date.now()}_${_.random(100000)}`;
    }
    return this.uniqueKey;
  }

  resetUniqueKey() {
    this.uniqueKey = undefined;
  }

  lockImportQuery() {
    this._importInProgress = true;
  }

  unlockImportQuery() {
    this._importInProgress = false;
  }

  isImportQueryInProgress() {
    return this._importInProgress;
  }

  issueImportQuery = (uploadings, uniqueKey = null) =>
    new Promise((resolve, reject) => {
      this.lockImportQuery();
      const query = new ImportQueryQuery();
      const msg = new ImportQueryMsg();
      const fileNames = [];
      let collectionId = null;

      if (uploadings && uploadings[0].collectionId) {
        collectionId = uploadings[0].collectionId;
      }
      _.each(uploadings, (__currentUploading) => {
        __currentUploading.imported = true;
        const cleanString = __currentUploading.name.replace(this._unsupportedCharacters, '-');
        debug('[issueImportQuery()] Cleaning file name. Result: ', cleanString);
        fileNames.push(cleanString);
      });

      msg.setUniqueKey(uniqueKey || uploadings[0].uniqueKey);
      msg.setFileNames(fileNames);
      if (collectionId) {
        msg.setCollectionId(collectionId);
      }

      query.message = msg;
      debug(`[issueImportQuery()] Issuing upload query for ${uploadings.length} files`);
      query.run().then(
        (resp) => {
          _.each(uploadings, (__currentUploading) => {
            __currentUploading.status = 'FINISHED';
          });
          debug('[issueImportQuery()] Success. Response: ', resp);
          window.render();
          this.unlockImportQuery();
          resolve(resp);
        },
        (error) => {
          _.each(uploadings, (__currentUploading) => {
            debug('[issueImportQuery()] ERROR. Response: ', error);
            __currentUploading.status = 'ERROR';
            __currentUploading.errorText =
              error.errorDetails && error.errorDetails.length > 0 ? error.errorDetails : t('UPLOAD_ERROR');
          });
          window.render();
          this.unlockImportQuery();
          reject(error);
        }
      );
    });

  watchUploadings(collectionUploadOperation = null, uploadings = null) {
    debug('[watchUploadings()] Begin. args: ', { collectionUploadOperation, uploadings });
    return new Promise((resolve, reject) => {
      const SECONDS_PER_UPLOAD = 60; // Adjust this as needed.
      const target = collectionUploadOperation || this;
      target._timerCount = 0;
      clearInterval(target.timer);

      target.timer = setInterval(() => {
        target._timerCount++;
        const arr = uploadings || _.values(this._uploadings);

        debug(`[watchUploadings()] timer iteration ${target._timerCount}`);
        if (arr.length === 0) {
          clearInterval(target.timer);
          return resolve([]);
        }

        /* if (target._timerCount > arr.length * SECONDS_PER_UPLOAD) {
          debug('[watchUploadings()] Timeout');
          clearInterval(target.timer);
          return reject(new Error('Timeout'));
        } */

        const successfulUploads = arr.filter((u) => u.status === 'UPLOADED' || u.status === 'FINISHED');
        const uploadsToImport = successfulUploads.filter((u) => u.imported === false);
        if (!collectionUploadOperation && !this.isImportQueryInProgress() && uploadsToImport.length > 0) {
          this.issueImportQuery(uploadsToImport)
            .then(resolve)
            .catch(reject);
        }

        const uploadedStatuses = ['FINISHED', 'ERROR'];
        if (collectionUploadOperation) {
          uploadedStatuses.push('UPLOADED');
        }
        if (_.every(arr, (u) => _.includes(uploadedStatuses, u.status))) {
          debug('[watchUploadings()] Success. Successful uploads: ', successfulUploads);
          clearInterval(target.timer);
          if (collectionUploadOperation) {
            resolve(successfulUploads);
          }
        }
      }, 1000);
    });
  }

  createOrUpdateCollectionUploadOperation(files, { collectionId, collectionName, autoSubmit = false }) {
    let operation = _.find(this._collectionUploadOperations, { collectionId });
    const uniqueKey = operation ? operation.uniqueKey : this.getUniqueKey();
    const uploadings = files.map((file) => this.createUploading(file, collectionId, true, uniqueKey));

    if (operation) {
      debug('[createOrUpdateCollectionUploadOperation()] Adding uploads to operation', uploadings, operation);
      operation.addUploadTask(uploadings);
    } else {
      debug('[createOrUpdateCollectionUploadOperation()] Creating new CollectionUploadOperation with args: ', {
        uploadings,
        collectionId,
        collectionName,
        autoSubmit
      });
      operation = new CollectionUploadOperation(uploadings, collectionId, collectionName, autoSubmit);
      this._collectionUploadOperations.push(operation);
      if (!window.gs.collectionUploadOperations)
        window.gs.collectionUploadOperations = [];
      window.gs.collectionUploadOperations = window.gs.collectionUploadOperations.concat(operation);
    }

    return operation.uploadPromises[operation.uploadPromises.length - 1];
  }

  removeCollectionUploadOperation(operation) {
    debug('[removeCollectionUploadOperation()] Removing operation from gs.collectionUploadOperations ', operation);
    _.remove(this._collectionUploadOperations, operation);
    if (!window.gs.collectionUploadOperations)
      window.gs.collectionUploadOperations = [];
    window.gs.collectionUploadOperations = window.gs.collectionUploadOperations.filter((o) => o !== operation);
    window.render();
    this.emit('collectionUploadOperationComplete');
  }

  getUploadingFromCollectionUploadOperations(id) {
    const uploadings = this._collectionUploadOperations.reduce((arr, op) => arr.concat(op.uploadings), []);
    return _.find(uploadings, { id });
  }

  _initWorker() {
    const worker = (this._worker = MainThreadUploadWorker());

    worker.onmessage = (event) => {
      debug('[worker.onmessage()] event: ', event.data);
      if (typeof event.data !== 'object') {
        return;
      }

      const { type, data = {} } = event.data;
      const uploading = this._uploadings[data.id] || this.getUploadingFromCollectionUploadOperations(data.id);
      if (uploading) {
        switch (type) {
          case 'UPLOAD_PROGRESS':
            uploading.loaded = data.loaded;
            uploading.total = data.total;

            // If render is called on every progress event then rendering will be too frequent and it will affect performance
            // This function limits the frequency of calling render to once per 100ms, and such frequency is ok for performance
            debouncedRender();
            break;
          case 'UPLOAD_SUCCESS':
            uploading.status = 'UPLOADED';
            debouncedRender();
            break;
          case 'UPLOAD_ERROR':
            debug('[issueImportQuery()] ERROR. event: ', event.data);
            uploading.status = 'ERROR';
            uploading.errorText = t('UPLOAD_ERROR');
            debouncedRender();
            break;
          case 'UPLOAD_ABORTED':
            this.removeUploading(data.id);
            break;
          case 'IMAGE_LOADED':
            uploading.preview = data.result;
            debouncedRender();
            break;
        }
      }
    };
  }

  _notifyWorker(type, data = {}) {
    const message = { type, data };

    if (!this._worker) {
      // Fallback in case FeatureDetectWorker never responds.
      this._initWorker(false);
    }

    this._worker.postMessage(message);
  }
}
